'use client';

import { FC, useEffect, useMemo, useState } from 'react';
import Image, { ImageProps } from 'next/image';
import { stripSizeSuffix, toHttps, toIwgOriginalUrl, toIwgResizedUrl } from '@/hotdesk-ui/helpers/image-url';

/**
 * Photo shown when a workspace/listing has no image that loads. A 3 KB WebP
 * on the production bucket: partner data (e.g. assets.regus.com) has broken
 * URLs we cannot fix, so every one of them falls back to this file.
 */
export const DEFAULT_WORKSPACE_IMAGE = 'https://hotdesk-assets.s3.eu-west-1.amazonaws.com/landing_pages/workspace_directory/defaultImage.webp';
const WORKSPACES_PATH_RE = /^(https?:\/\/[^/]+)\/workspaces\//i;
const swapWorkspacesToListings = (src: string) => src.replace(WORKSPACES_PATH_RE, '$1/listings/');
export const buildCandidates = (src: string): string[] => {
  src = toHttps(src);
  // IWG photos are requested resized first: their suffixed URL always 404s.
  // An unsuffixed IWG URL gets the `md` width. Only if Cloudinary fails is
  // the 5-7 MB original used, so the real photo still shows.
  const iwgResized = toIwgResizedUrl(src) ?? toIwgResizedUrl(`${stripSizeSuffix(src)}/md`);
  if (iwgResized) {
    const original = toIwgOriginalUrl(src);
    return original && original !== iwgResized ? [iwgResized, original] : [iwgResized];
  }
  const ordered: string[] = [src];
  const swapped = swapWorkspacesToListings(src);
  if (swapped !== src) ordered.push(swapped);
  const stripped = stripSizeSuffix(src);
  if (stripped !== src) ordered.push(stripped);
  if (swapped !== src && stripped !== src) {
    const swappedStripped = stripSizeSuffix(swapped);
    if (swappedStripped !== swapped) ordered.push(swappedStripped);
  }
  return ordered;
};
const loadsInBrowser = (src: string) => new Promise<boolean>(resolve => {
  const img = new window.Image();
  img.onload = () => resolve(true);
  img.onerror = () => resolve(false);
  img.src = src;
});

/**
 * Resolves `true` when `src` (or one of its CDN fallback variants) loads.
 * Used to drop broken images from galleries before they are rendered.
 */
export const probeImage = async (src: string): Promise<boolean> => {
  for (const candidate of buildCandidates(src)) {
    if (await loadsInBrowser(candidate)) return true;
  }
  return false;
};
export interface SafeImageProps extends Omit<ImageProps, 'src' | 'onError'> {
  src: string;
  fallbackSrc?: string;
  /** Fired once every candidate URL has failed, before the fallback renders. */
  onAllFailed?: () => void;
  /**
   * Smaller file for phones. Rendered as a `<picture>` source, so the
   * browser downloads exactly one of the two (no JS, SSR-safe).
   */
  mobileSrc?: string;
  /** Media query that selects `mobileSrc`. */
  mobileMedia?: string;
}

/**
 * Wraps next/image with a layered error fallback for CDN images that may
 * 404 on a specific size variant or on the wrong workspaces/listings prefix.
 * Walks: original → /listings/ swap → suffix-stripped → swap+stripped →
 * static fallback asset.
 */
const SafeImage: FC<SafeImageProps> = ({
  src,
  fallbackSrc = DEFAULT_WORKSPACE_IMAGE,
  onAllFailed,
  mobileSrc,
  mobileMedia = '(max-width: 640px)',
  ...rest
}) => {
  const candidates = useMemo(() => buildCandidates(src), [src]);
  const mobileCandidates = useMemo(() => mobileSrc ? buildCandidates(mobileSrc) : null, [mobileSrc]);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    setAttempt(0);
  }, [src, mobileSrc]);
  const currentSrc = candidates[attempt] ?? fallbackSrc;
  const handleError = () => {
    if (attempt + 1 === candidates.length) onAllFailed?.();
    setAttempt(prev => prev + 1);
  };
  if (!mobileCandidates) {
    return <Image {...rest} src={currentSrc} onError={handleError} />;
  }

  // next/image `priority` preloads `src` with no media query, so a phone
  // would fetch the desktop file as well as its own. Mark the <img> high
  // priority instead: the browser then fetches only the file it picks.
  const {
    priority,
    loading,
    fetchPriority,
    ...imageProps
  } = rest;

  // An error on whichever file the browser picked fires on the <img>, so
  // both lists step to their next candidate together.
  return <picture style={{
    display: 'contents'
  }} data-sentry-element="picture" data-sentry-component="SafeImage" data-sentry-source-file="SafeImage.tsx">
            <source media={mobileMedia} srcSet={mobileCandidates[attempt] ?? fallbackSrc} />
            <Image {...imageProps} loading={priority ? 'eager' : loading} fetchPriority={priority ? 'high' : fetchPriority} src={currentSrc} onError={handleError} data-sentry-element="Image" data-sentry-source-file="SafeImage.tsx" />
        </picture>;
};
export default SafeImage;